import { getClientUser } from "@/services/auth/clientSession";
import { encryptionService } from "./encryptionService";
import { API_ENDPOINTS, getAuthHeader } from "@/config/api";
import { getAuthToken } from "./assets/assetHelpers";
import { authService } from "./auth/index";
import { validatePanNumber } from "@/utils/panValidation";
import { secureRandomInt } from "@/utils/secureRandom";
import { validateInvitableEmailSecurity } from "../../shared-utils/emailSignupSecurity.mjs";

function formatSecurityLogFailure(errorData: Record<string, unknown>, status: number): string {
  const errorField = errorData.error;
  const messageField = errorData.message;
  if (typeof errorField === "string" && errorField.trim()) {
    return errorField;
  }
  if (typeof messageField === "string" && messageField.trim()) {
    return messageField;
  }
  return `Security log request failed (HTTP ${status})`;
}

/**
 * Enhanced security service for audit logging, rate limiting, and validation
 */
export class SecurityService {
  private static instance: SecurityService;

  public static getInstance(): SecurityService {
    if (!SecurityService.instance) {
      SecurityService.instance = new SecurityService();
    }
    return SecurityService.instance;
  }

  /**
   * Log security events for audit trail
   */
  async logSecurityEvent(
    eventType: string,
    resourceType: string,
    resourceId: string,
    action: string,
    metadata: Record<string, unknown> = {}
  ): Promise<void> {
    try {
      const headers = await authService.getAuthHeaders();

      const response = await fetch(API_ENDPOINTS.AUTH.SECURITY_LOG_EVENT, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          eventType,
          resourceType,
          resourceId,
          action,
          metadata,
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        console.warn(
          "Failed to log security event:",
          formatSecurityLogFailure(errorData, response.status)
        );
      }
    } catch (error) {
      console.warn(
        "Security logging failed:",
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }

  /**
   * Check rate limits for sensitive operations
   */
  async checkRateLimit(
    actionType: string,
    maxAttempts: number = 5,
    windowMinutes: number = 15
  ): Promise<boolean> {
    try {
      const { data: user } = await getClientUser();
      if (!user.user) return false;

      const headers = await authService.getAuthHeaders();

      const response = await fetch(API_ENDPOINTS.AUTH.SECURITY_RATE_LIMIT_CHECK, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          actionType,
          maxAttempts,
          windowMinutes,
        }),
      });

      if (!response.ok) {
        console.error("Rate limit check failed");
        return false; // Fail closed for security
      }

      const result = await response.json();
      return result.allowed === true;
    } catch (error) {
      console.error("Rate limit service error:", error);
      return false;
    }
  }

  /**
   * Get rate limit details including time remaining
   */
  async getRateLimitInfo(
    actionType: string,
    maxAttempts: number = 5,
    windowMinutes: number = 15
  ): Promise<{ allowed: boolean; timeRemaining?: number; attemptsUsed?: number }> {
    try {
      const { data: user } = await getClientUser();
      if (!user.user) return { allowed: false };

      const token = await getAuthToken();
      const headers = getAuthHeader(token || undefined);

      const response = await fetch(
        `${API_ENDPOINTS.AUTH.SECURITY_RATE_LIMIT_INFO}?actionType=${encodeURIComponent(actionType)}&maxAttempts=${maxAttempts}&windowMinutes=${windowMinutes}`,
        {
          method: "GET",
          headers,
          credentials: "include",
        }
      );

      if (!response.ok) {
        console.error("Rate limit info fetch failed");
        return { allowed: true }; // Fail open
      }

      const result = await response.json();
      return {
        allowed: result.allowed === true,
        timeRemaining: result.timeRemaining,
        attemptsUsed: result.attemptsUsed,
      };
    } catch (error) {
      console.error("Rate limit info service error:", error);
      return { allowed: true }; // Fail open
    }
  }

  /**
   * Validate PAN number format with enhanced security
   */
  validatePANNumber(pan: string): { isValid: boolean; error?: string } {
    const result = validatePanNumber(pan, { required: true });
    if (!result.valid) {
      return { isValid: false, error: result.error };
    }

    const cleanPAN = result.normalized ?? "";

    // Additional security checks for suspicious patterns
    const suspiciousPatterns = [
      /(.)\1{4,}/, // Repeated characters (5 or more)
      /^[A-Z]{5}0000[A-Z]$/, // All zeros in numeric part
      /^[A-Z]{5}1111[A-Z]$/, // All ones in numeric part
      /^AAAAA\d{4}A$/, // All A's in letter parts
    ];

    for (const pattern of suspiciousPatterns) {
      if (pattern.test(cleanPAN)) {
        return { isValid: false, error: "Suspicious PAN pattern detected" };
      }
    }

    return { isValid: true };
  }

  /**
   * Validate email format with security considerations
   */
  validateEmail(email: string): { isValid: boolean; error?: string } {
    if (!email) {
      return { isValid: false, error: "Email is required" };
    }

    // Basic email validation with security in mind
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, error: "Invalid email format" };
    }

    // Check for common malicious patterns
    const suspiciousChecks: Array<(value: string) => boolean> = [
      (value) => /script/i.test(value),
      (value) => {
        const open = value.indexOf("<");
        return open !== -1 && value.includes(">", open + 1);
      },
      (value) => /javascript:/i.test(value),
      (value) => /data:/i.test(value),
      (value) => /vbscript:/i.test(value),
    ];

    for (const check of suspiciousChecks) {
      if (check(email)) {
        return { isValid: false, error: "Invalid characters in email" };
      }
    }

    return { isValid: true };
  }

  /**
   * Validate invite email: format checks plus signup-equivalent disposable/pentest blocklist.
   */
  validateInviteEmail(email: string): { isValid: boolean; error?: string } {
    const formatCheck = this.validateEmail(email);
    if (!formatCheck.isValid) {
      return formatCheck;
    }

    const securityCheck = validateInvitableEmailSecurity(email);
    if (!securityCheck.allowed) {
      return {
        isValid: false,
        error: securityCheck.reason || "This email address is not allowed.",
      };
    }

    return { isValid: true };
  }

  /**
   * Enhanced input sanitization to prevent XSS and injection attacks
   */
  sanitizeInput(input: string): string {
    if (!input) return "";

    return input
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#x27;")
      .replace(/\//g, "&#x2F;")
      .replace(/\\/g, "&#x5C;")
      .replace(/`/g, "&#x60;")
      .replace(/=/g, "&#x3D;")
      .replace(/\(/g, "&#x28;")
      .replace(/\)/g, "&#x29;")
      .replace(/\{/g, "&#x7B;")
      .replace(/\}/g, "&#x7D;")
      .replace(/\[/g, "&#x5B;")
      .replace(/\]/g, "&#x5D;")
      .replace(/&(?!#?\w+;)/g, "&amp;")
      .trim();
  }

  /**
   * Validate and sanitize file uploads
   */
  validateFileUpload(
    file: File,
    allowedTypes: string[],
    maxSize: number
  ): { isValid: boolean; error?: string } {
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: "Invalid file type" };
    }

    // Check file size
    if (file.size > maxSize) {
      return { isValid: false, error: `File size exceeds ${maxSize} bytes` };
    }

    // Check for suspicious file names
    const suspiciousPatterns = [
      /\.(exe|bat|cmd|scr|vbs|js|jar|com|pif)$/i,
      /[<>:"|?*]/,
      /^(con|prn|aux|nul|com\d|lpt\d)(\.|$)/i,
    ];

    for (const pattern of suspiciousPatterns) {
      if (pattern.test(file.name)) {
        return { isValid: false, error: "Suspicious file name detected" };
      }
    }

    return { isValid: true };
  }

  /**
   * Generate secure random token
   */
  generateSecureToken(length: number = 32): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(secureRandomInt(chars.length));
    }
    return result;
  }

  /**
   * Encrypt PAN number for family member
   */
  async encryptPANForMember(
    memberId: string,
    panNumber: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Validate PAN first
      const validation = this.validatePANNumber(panNumber);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      // Check rate limit
      const canProceed = await this.checkRateLimit("encrypt_pan", 10, 15);
      if (!canProceed) {
        return { success: false, error: "Too many encryption attempts. Please wait." };
      }

      // Encrypt PAN data (placeholder - not used, encryption happens in family-service)
      await encryptionService.encryptPANData(userId, panNumber);

      // Note: PAN encryption for family members should be handled by family-service
      // This endpoint is a placeholder - actual encryption happens client-side
      // and the encrypted data is sent to family-service
      // For now, we'll log the event but the actual update should go through family-service

      await this.logSecurityEvent("ENCRYPTION_SUCCESS", "family_member", memberId, "encrypt_pan");

      // Return success - actual database update should be done through family-service
      return { success: true };
    } catch (error) {
      console.error("PAN encryption failed:", error);
      return { success: false, error: "Encryption failed" };
    }
  }

  /**
   * Get client IP address (best effort)
   */
  private async getClientIP(): Promise<string | null> {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip || null;
    } catch {
      return null;
    }
  }
}

export const securityService = SecurityService.getInstance();
